/* ── PAG hooks ────────────────────────────────────────────────────────────
   React custom hooks shared by Privilege Access Governance screens.
   Each hook wraps a pure helper from pagHelpers.ts with the appropriate
   memoisation or side-effect boundary. */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchPagSourceSystemFilter,
  type PagReviewRequestRow,
  type PagSourceSystemFilter,
} from "../services/pagApi";
import {
  extractSelectedAccountNames,
  getResourceValuesForSystems,
  resourceOptions,
  sourceSystemOptions,
} from "../helpers/pagHelpers";
import { useToastStore } from "../../../../stores/toastStore";

/* ── Selection ──────────────────────────────────────────────────────── */

/**
 * Derives the unique, non-empty account names that correspond to the
 * currently-selected row IDs.
 *
 * @param rows       The full list of review-request rows currently on screen.
 * @param selectedIds The set of `reviewId` values the user has checked.
 * @returns A stable, deduplicated string array – only recomputed when
 *          `rows` or `selectedIds` change.
 */
export const useSelectedAccountNames = (
  rows: PagReviewRequestRow[],
  selectedIds: number[],
): string[] =>
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useMemo(
    () => extractSelectedAccountNames(rows, selectedIds),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, selectedIds],
  );

/* ── Scope filter ───────────────────────────────────────────────────── */

/**
 * The Source System / Resources filter every PAG screen scopes itself by.
 *
 * The dropdowns only stage a choice: nothing re-reads until `apply()` runs,
 * which is what the Filter button calls. `scopeKey` is a stable string so an
 * effect can depend on it without refiring every render, and `reloadToken`
 * changes on every apply so pressing Filter refreshes even when the selection
 * is unchanged.
 */
export const usePagScopeFilter = () => {
  const [systems, setSystems] = useState<PagSourceSystemFilter[]>([]);
  const [selectedSystems, setSelectedSystems] = useState<string[]>([]);
  const [resources, setResources] = useState<string[]>([]);
  const [scopeKey, setScopeKey] = useState("");
  const [reloadToken, setReloadToken] = useState(0);

  const initialSelectionAppliedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    fetchPagSourceSystemFilter()
      .then((response) => {
        if (cancelled) return;
        const list = Array.isArray(response) ? response : [];
        setSystems(list);
        if (!initialSelectionAppliedRef.current && list.length > 0) {
          initialSelectionAppliedRef.current = true;
          const allSystems = sourceSystemOptions(list)
            .map((opt) => opt.value)
            .filter(Boolean);
          const allResources = resourceOptions(list, allSystems).map((opt) => opt.value);
          const ids = allResources.map(Number).filter((id) => Number.isFinite(id) && id > 0);
          setSelectedSystems(allSystems);
          setResources(allResources);
          if (ids.length > 0) {
            setScopeKey(ids.join(","));
          }
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        useToastStore
          .getState()
          .showError(
            "Could not load source systems",
            err instanceof Error ? err.message : "Please retry in a moment.",
          );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const stagedScopeKey = useMemo(() => {
    const chosen = resources.length
      ? resources
      : resourceOptions(systems, selectedSystems).map((option) => option.value);
    const ids = chosen.map(Number).filter((id) => Number.isFinite(id) && id > 0);
    return ids.join(",");
  }, [resources, systems, selectedSystems]);

  /** The instance ids the applied scope covers. */
  const sourceSystemIds = useMemo(
    () =>
      scopeKey
        .split(",")
        .map(Number)
        .filter((id) => Number.isFinite(id) && id > 0),
    [scopeKey],
  );

  /** Picking a system auto-selects all its related resources. */
  const changeSystems = useCallback(
    (nextSystems: string[]) => {
      setSelectedSystems(nextSystems);
      setResources((prevResources) => {
        if (nextSystems.length === 0) return [];
        const prevSystemSet = new Set(selectedSystems);
        const nextSystemSet = new Set(nextSystems);

        const addedSystems = nextSystems.filter((s) => !prevSystemSet.has(s));
        const removedSystems = selectedSystems.filter((s) => !nextSystemSet.has(s));

        const addedResourceIds = new Set(
          getResourceValuesForSystems(systems, addedSystems),
        );
        const removedResourceIds = new Set(
          getResourceValuesForSystems(systems, removedSystems),
        );

        const kept = prevResources.filter((id) => !removedResourceIds.has(id));
        const nextCombined = Array.from(new Set([...kept, ...addedResourceIds]));

        if (nextCombined.length === 0) {
          return getResourceValuesForSystems(systems, nextSystems);
        }
        return nextCombined;
      });
    },
    [systems, selectedSystems],
  );

  const apply = useCallback(() => {
    setScopeKey(stagedScopeKey);
    setReloadToken((token) => token + 1);
  }, [stagedScopeKey]);

  const clear = useCallback(() => {
    setSelectedSystems([]);
    setResources([]);
    setScopeKey("");
    setReloadToken((token) => token + 1);
  }, []);

  return {
    systems,
    selectedSystems,
    resources,
    setResources,
    changeSystems,
    sourceSystemIds,
    scopeKey,
    reloadToken,
    apply,
    clear,
    systemOptions: sourceSystemOptions(systems),
    resourceOptions: resourceOptions(systems, selectedSystems),
  };
};
